import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { AppWindow, ArrowLeft, Code2, ExternalLink, HeartPulse, Link2, Plus, Save, Settings2, ShieldCheck, Trash2, XCircle } from "lucide-react";
import { useApi, useHandoff, usePermission } from "@ncp/api-client";
import { PlatformIcon, tileClasses } from "@ncp/assistant-sdk";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import {
  Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field,
  InlineAlert, Input, NativeSelect, Skeleton, Switch, Tabs, TabsList, TabsTrigger, Textarea, Tooltip, cn, copyText, notifyDone, notifySaved, toast,
} from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { CodeEditor } from "../components/CodeEditor";
import { UnsavedChangesDialog, useUnsavedChanges } from "../lib/useUnsavedChanges";
import { stableJson } from "../features/forms/model";
import { usePermissionCatalogue } from "../features/access/api";
import { useFeatureFlags } from "../features/settings/api";
import { ICON_SUGGESTIONS, RefPicker } from "../features/workspaces/RefPicker";
import {
  SOLUTION_KINDS, previewUrl, solutionBodyOf, templateProblems, useDeepLinkScopes, useSolution, useSolutionActions,
  type DeepLinkScope, type DeepLinkTemplate, type Solution, type SolutionBody, type SolutionKind,
} from "../features/solutions/api";
import { FlagBadge, HealthCell } from "./SolutionsPage";

type Tab = "general" | "connection" | "access" | "links" | "embed" | "settings";
const TABS: Array<{ key: Tab; icon: typeof Settings2 }> = [
  { key: "general", icon: Settings2 }, { key: "connection", icon: HeartPulse }, { key: "access", icon: ShieldCheck }, { key: "links", icon: Link2 }, { key: "embed", icon: Code2 }, { key: "settings", icon: Settings2 },
];

/** One surface of the registry: identity, connection and health, who may see it, its deep-link templates, embedding and settings. */
export function SolutionPage() {
  const { key = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab | null) ?? "general";
  const setTab = (next: Tab) => setParams((p) => { p.set("tab", next); return p; }, { replace: true });
  const solution = useSolution(key);
  if (solution.isLoading) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-[60vh]" /></div>;
  if (solution.isError || !solution.data) return <Missing solutionKey={key} />;
  return <Editor key={solution.data.key + solution.data.updatedAt} solution={solution.data} tab={tab} onTab={setTab} />;
}

function Missing({ solutionKey }: { solutionKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBreadcrumbs([{ label: t("admin:nav.solutions"), href: "/solutions" }, { label: solutionKey }]);
  return <EmptyState icon={XCircle} title={t("admin:solutions.notFound")} description={t("admin:solutions.notFoundBody", { key: solutionKey })} action={<Button variant="outline" onClick={() => navigate("/solutions")}><ArrowLeft /> {t("admin:common.back")}</Button>} />;
}

function Editor({ solution, tab, onTab }: { solution: Solution; tab: Tab; onTab: (t: Tab) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can("platform.workspaces.write");
  const actions = useSolutionActions();
  const scopes = useDeepLinkScopes();

  const [body, setBody] = useState<SolutionBody>(solutionBodyOf(solution));
  const savedJson = useRef(stableJson(solutionBodyOf(solution)));
  const dirty = stableJson(body) !== savedJson.current;
  const blocker = useUnsavedChanges(dirty);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [settingsText, setSettingsText] = useState(() => JSON.stringify(body.settings ?? {}, null, 2));
  const settingsError = useMemo(() => { try { const v: unknown = JSON.parse(settingsText || "{}"); return v && typeof v === "object" && !Array.isArray(v) ? null : t("admin:solutions.settings.notObject"); } catch (e) { return (e as Error).message; } }, [settingsText, t]);
  const linkProblems = useMemo(() => body.deepLinks.map((l) => (scopes.data ? templateProblems(l, scopes.data, body.deepLinks) : [])), [body.deepLinks, scopes.data]);
  const invalidLinks = linkProblems.filter((p) => p.length > 0).length;

  const title = (locale === "ar" && body.nameAr ? body.nameAr : body.name) || solution.key;
  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.solutions"), href: "/solutions" }, { label: title }], [t, title]));
  useHelpTopic({ title: t("admin:solutions.editor.helpTitle"), docsAnchor: "solutions", body: (<><p>{t("admin:solutions.help.what")}</p><p>{t("admin:solutions.help.links")}</p><p>{t("admin:solutions.help.health")}</p></>) });

  const patch = (p: Partial<SolutionBody>) => { if (canWrite) setBody((b) => ({ ...b, ...p })); };

  const save = useCallback(async () => {
    if (!canWrite || !dirty) return;
    if (!body.name.trim()) { toast.error(t("admin:solutions.editor.nameRequired")); return; }
    if (settingsError) { toast.error(t("admin:solutions.settings.invalid")); onTab("settings"); return; }
    if (invalidLinks) { toast.error(t("admin:solutions.links.fixFirst", { count: invalidLinks })); onTab("links"); return; }
    try {
      const saved = await actions.update.mutateAsync({ key: solution.key, body });
      savedJson.current = stableJson(solutionBodyOf(saved));
      setBody(solutionBodyOf(saved));
      notifySaved(saved, t("admin:common.saved"));
    } catch { /* useSave reported it */ }
  }, [actions.update, body, canWrite, dirty, invalidLinks, onTab, settingsError, solution.key, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const probe = () => actions.probe.mutate(solution.key, {
    onSuccess: (r) => (r.ok ? notifyDone(t("admin:solutions.probe.up", { name: solution.name, ms: r.latencyMs })) : toast.error(t("admin:solutions.probe.down", { name: solution.name }), { description: r.error ?? `HTTP ${r.statusCode}` })),
  });
  usePaletteActions(useMemo(() => canWrite ? [
    { id: "solution.save", label: t("admin:common.save"), icon: Save, permission: "platform.workspaces.write", run: () => void save() },
    { id: "solution.probe", label: t("admin:solutions.probe.one"), icon: HeartPulse, permission: "platform.workspaces.write", run: probe },
  ] : [], [canWrite, save, t])); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    try { notifySaved(await actions.remove.mutateAsync(solution.key), t("admin:solutions.editor.deleted")); navigate("/solutions"); } catch { /* useSave reported it */ }
  };

  return (
    <div className="-mt-2">
      <UnsavedChangesDialog blocker={blocker} />
      <div className="sticky top-14 z-20 -mx-4 mb-4 border-b border-sand-200 bg-background/90 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/solutions")}><ArrowLeft className="rtl:rotate-180" /></Button>
          <span className={tileClasses("sand", "size-9")}><PlatformIcon name={body.icon} fallback={AppWindow} className="size-4" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h1 className="truncate text-base font-semibold">{title}</h1>
              <Badge variant="outline" size="sm" className="font-mono">{solution.key}</Badge>
              <Badge variant="outline" size="sm">{t(`admin:solutions.kind.${body.kind}`)}</Badge>
              {solution.isBuiltIn ? <Badge variant="outline" size="sm">{t("admin:common.builtIn")}</Badge> : null}
              {!body.enabled ? <Badge variant="default" size="sm">{t("admin:common.disabled")}</Badge> : null}
              {dirty ? <Badge variant="warning" size="sm">{t("admin:common.unsaved")}</Badge> : null}
            </div>
            <HealthCell s={solution} />
          </div>
          <div className="ms-auto flex flex-wrap items-center gap-1">
            {canWrite && solution.probeUrl ? <Button size="sm" variant="ghost" loading={actions.probe.isPending} onClick={probe}><HeartPulse /> {t("admin:solutions.probe.one")}</Button> : null}
            {canWrite && !solution.isBuiltIn ? <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => setConfirmDelete(true)}><Trash2 /> {t("admin:common.delete")}</Button> : null}
            {canWrite ? <Button size="sm" variant={dirty ? "gold" : "outline"} loading={actions.update.isPending} disabled={!dirty} onClick={() => void save()}><Save /> {t("admin:common.save")}</Button> : null}
          </div>
        </div>
        <Tabs value={tab} onValueChange={(v) => onTab(v as Tab)} className="mt-2">
          <TabsList>{TABS.map(({ key: k, icon: Icon }) => (
            <TabsTrigger key={k} value={k}><Icon className="size-3.5" /> {t(`admin:solutions.tabs.${k}`)}
              {k === "links" ? <Badge variant={invalidLinks ? "danger" : "outline"} size="sm" className="ms-1">{body.deepLinks.length}</Badge> : null}
              {k === "settings" && settingsError ? <Badge variant="danger" size="sm" className="ms-1">!</Badge> : null}
            </TabsTrigger>
          ))}</TabsList>
        </Tabs>
      </div>

      {!canWrite ? <InlineAlert tone="info" className="mb-3">{t("admin:common.permissionDenied")}</InlineAlert> : null}
      {solution.isBuiltIn ? <InlineAlert tone="info" className="mb-3">{t("admin:solutions.editor.builtInHint")}</InlineAlert> : null}
      {solution.sourcePackageKey ? <InlineAlert tone="info" className="mb-3">{t("admin:solutions.editor.fromPackage", { key: solution.sourcePackageKey })}</InlineAlert> : null}

      {tab === "general" ? <GeneralTab body={body} patch={patch} canWrite={canWrite} solution={solution} /> : null}
      {tab === "connection" ? <ConnectionTab body={body} patch={patch} canWrite={canWrite} solution={solution} onProbe={probe} probing={actions.probe.isPending} /> : null}
      {tab === "access" ? <AccessTab body={body} patch={patch} canWrite={canWrite} solution={solution} /> : null}
      {tab === "links" ? <LinksTab body={body} patch={patch} canWrite={canWrite} scopes={scopes.data ?? []} problems={linkProblems} /> : null}
      {tab === "embed" ? <EmbedTab solution={solution} /> : null}
      {tab === "settings" ? (
        <Card>
          <CardHeader><CardTitle>{t("admin:solutions.settings.title")}</CardTitle><CardDescription>{t("admin:solutions.settings.hint")}</CardDescription></CardHeader>
          <CardContent className="space-y-2">
            <CodeEditor value={settingsText} readOnly={!canWrite} height="360px" onChange={(v) => { setSettingsText(v); try { const parsed: unknown = JSON.parse(v || "{}"); if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) patch({ settings: parsed as Record<string, unknown> }); } catch { /* shown below */ } }} />
            {settingsError ? <p className="text-xs text-danger-700">{settingsError}</p> : <p className="text-xs text-muted-foreground">{t("admin:solutions.settings.seedVersion")}</p>}
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent size="sm">
          <DialogHeader><DialogTitle>{t("admin:solutions.editor.deleteTitle", { name: title })}</DialogTitle><DialogDescription>{solution.usedByWorkspaceKeys.length ? t("admin:solutions.editor.deleteInUse", { workspaces: solution.usedByWorkspaceKeys.join(", ") }) : t("admin:solutions.editor.deleteBody")}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>{t("admin:common.cancel")}</Button>
            <Button variant="destructive" loading={actions.remove.isPending} onClick={remove}><Trash2 /> {t("admin:common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TabProps { body: SolutionBody; patch: (p: Partial<SolutionBody>) => void; canWrite: boolean; solution: Solution }

function GeneralTab({ body, patch, canWrite, solution }: TabProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.editor.identity")}</CardTitle><CardDescription>{t("admin:solutions.editor.identityHint")}</CardDescription></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:common.name")} required><Input value={body.name} readOnly={!canWrite} onChange={(e) => patch({ name: e.target.value })} /></Field>
          <Field label={t("admin:common.nameAr")}><Input value={body.nameAr ?? ""} dir="rtl" readOnly={!canWrite} onChange={(e) => patch({ nameAr: e.target.value || null })} /></Field>
          <Field label={t("admin:common.description")} className="sm:col-span-2"><Textarea value={body.description ?? ""} readOnly={!canWrite} onChange={(e) => patch({ description: e.target.value || null })} className="min-h-[64px]" /></Field>
          <Field label={t("admin:common.descriptionAr")} className="sm:col-span-2"><Textarea value={body.descriptionAr ?? ""} dir="rtl" readOnly={!canWrite} onChange={(e) => patch({ descriptionAr: e.target.value || null })} className="min-h-[64px]" /></Field>
          <Field label={t("admin:solutions.fields.kind")} hint={t(`admin:solutions.kindHint.${body.kind}`)}>
            <NativeSelect value={body.kind} disabled={!canWrite || solution.isBuiltIn} onChange={(e) => patch({ kind: e.target.value as SolutionKind })}>{SOLUTION_KINDS.map((k) => <option key={k} value={k}>{t(`admin:solutions.kind.${k}`)}</option>)}</NativeSelect>
          </Field>
          <Field label={t("admin:solutions.fields.icon")} hint={t("admin:solutions.fields.iconHint")}>
            <div className="flex items-center gap-2"><span className={tileClasses("sand", "size-9")}><PlatformIcon name={body.icon} fallback={AppWindow} className="size-4" /></span><Input list="solution-icons" value={body.icon} dir="ltr" readOnly={!canWrite} onChange={(e) => patch({ icon: e.target.value })} className="font-mono" /><datalist id="solution-icons">{[...ICON_SUGGESTIONS, "AppWindow", "PanelRight", "MessagesSquare", "Mail", "Smartphone"].map((i) => <option key={i} value={i} />)}</datalist></div>
          </Field>
          <Field label={t("admin:solutions.fields.sortOrder")} hint={t("admin:solutions.fields.sortOrderHint")}><Input type="number" value={body.sortOrder} readOnly={!canWrite} onChange={(e) => patch({ sortOrder: Number(e.target.value) })} /></Field>
          <Field label={t("admin:common.enabled")} hint={t("admin:solutions.fields.enabledHint")}><div className="flex h-10 items-center"><Switch checked={body.enabled} disabled={!canWrite} onCheckedChange={(v) => patch({ enabled: v })} /></div></Field>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.editor.usage")}</CardTitle><CardDescription>{t("admin:solutions.editor.usageHint")}</CardDescription></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">{t("admin:solutions.usedBy")}</p>
            {solution.usedByWorkspaceKeys.length ? <div className="mt-1 flex flex-wrap gap-1">{solution.usedByWorkspaceKeys.map((k) => <Badge key={k} variant="outline" className="font-mono">{k}</Badge>)}</div> : <p className="mt-1 text-xs text-muted-foreground">{solution.key === "assistant" || solution.key === "admin" ? t("admin:solutions.everywhere") : t("admin:solutions.editor.unused")}</p>}
          </div>
          <div className="flex items-center justify-between text-xs"><span className="text-muted-foreground">{t("admin:solutions.threadsMonth")}</span><span className="tabular font-medium">{solution.threadsMonth}</span></div>
          <div className="flex items-center justify-between text-xs"><span className="text-muted-foreground">{t("admin:common.updated")}</span><span>{formatRelative(solution.updatedAt, locale)}</span></div>
        </CardContent>
      </Card>
    </div>
  );
}

function ConnectionTab({ body, patch, canWrite, solution, onProbe, probing }: TabProps & { onProbe: () => void; probing: boolean }) {
  const { t } = useTranslation();
  const { open } = useHandoff();
  const urlError = (v?: string | null) => (v && !/^https?:\/\/\S+$/i.test(v) ? t("admin:solutions.create.urlInvalid") : undefined);
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.editor.connection")}</CardTitle><CardDescription>{t("admin:solutions.editor.connectionHint")}</CardDescription></CardHeader>
        <CardContent className="grid gap-3">
          <Field label={t("admin:solutions.fields.baseUrl")} hint={solution.isBuiltIn ? t("admin:solutions.fields.baseUrlBuiltIn") : t("admin:solutions.fields.baseUrlHint")} error={urlError(body.baseUrl)}>
            <div className="flex gap-2">
              <Input value={body.baseUrl} dir="ltr" className="font-mono" readOnly={!canWrite || solution.isBuiltIn} onChange={(e) => patch({ baseUrl: e.target.value.trim() })} />
              {body.baseUrl ? <Button variant="outline" size="icon" aria-label={t("admin:common.open")} onClick={() => (body.supportsHandoff ? open(body.baseUrl) : void window.open(body.baseUrl, "_blank", "noopener"))}><ExternalLink /></Button> : null}
            </div>
          </Field>
          <Field label={t("admin:solutions.fields.healthUrl")} hint={t("admin:solutions.fields.healthUrlHint")} error={urlError(body.healthUrl)}>
            <Input value={body.healthUrl ?? ""} dir="ltr" className="font-mono" placeholder={body.baseUrl || "https://…/health"} readOnly={!canWrite} onChange={(e) => patch({ healthUrl: e.target.value.trim() || null })} />
          </Field>
          <Field label={t("admin:solutions.fields.handoff")} hint={t("admin:solutions.fields.handoffHint")}><div className="flex h-10 items-center"><Switch checked={body.supportsHandoff} disabled={!canWrite} onCheckedChange={(v) => patch({ supportsHandoff: v })} /></div></Field>
          {body.kind === "Channel" || body.kind === "Embedded" ? (
            <Field label={t("admin:solutions.fields.adapter")} hint={t("admin:solutions.fields.adapterHint")}>
              <Input value={body.adapterKey ?? ""} dir="ltr" className="font-mono" placeholder={body.kind === "Channel" ? "teams" : "web-component"} readOnly={!canWrite} onChange={(e) => patch({ adapterKey: e.target.value.trim() || null })} />
            </Field>
          ) : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.health.title")}</CardTitle><CardDescription>{t("admin:solutions.health.hint")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <HealthCell s={solution} showUrl />
          {!solution.probeUrl ? <p className="text-xs text-muted-foreground">{t("admin:solutions.health.noTarget")}</p> : null}
          {canWrite && solution.probeUrl ? <Button variant="outline" size="sm" loading={probing} onClick={onProbe}><HeartPulse /> {t("admin:solutions.probe.one")}</Button> : null}
          <p className="text-[11px] text-muted-foreground">{t("admin:solutions.health.alerts")}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function AccessTab({ body, patch, canWrite, solution }: TabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const catalogue = usePermissionCatalogue();
  const flags = useFeatureFlags();
  const options = useMemo(() => (catalogue.data ?? []).flatMap((a) => a.permissions.map((p) => ({ key: p.key, name: p.name || p.key, hint: p.key, description: p.description }))), [catalogue.data]);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.editor.permissions")}</CardTitle><CardDescription>{t("admin:solutions.editor.permissionsHint")}</CardDescription></CardHeader>
        <CardContent>{catalogue.isLoading ? <Skeleton className="h-40" /> : <RefPicker options={options} value={body.requiredPermissions} onChange={(v) => patch({ requiredPermissions: v })} readOnly={!canWrite} emptyLabel={t("admin:solutions.editor.noPermissions")} />}</CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.fields.flag")}</CardTitle><CardDescription>{t("admin:solutions.editor.flagHint")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:solutions.fields.flagKey")}>
            <Input list="solution-flags" value={body.flagKey ?? ""} dir="ltr" className="font-mono" placeholder={`solution.${solution.key}.enabled`} readOnly={!canWrite} onChange={(e) => patch({ flagKey: e.target.value.trim() || null })} />
          </Field>
          <datalist id="solution-flags">{(flags.data ?? []).map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}</datalist>
          {body.flagKey === (solution.flagKey ?? null) ? <FlagBadge s={solution} /> : null}
          {body.flagKey && !(flags.data ?? []).some((f) => f.key === body.flagKey) ? <InlineAlert tone="warning">{t("admin:solutions.editor.flagMissing")}</InlineAlert> : null}
          <Button variant="ghost" size="sm" onClick={() => navigate("/settings/flags")}><ExternalLink /> {t("admin:solutions.editor.manageFlags")}</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function LinksTab({ body, patch, canWrite, scopes, problems }: { body: SolutionBody; patch: (p: Partial<SolutionBody>) => void; canWrite: boolean; scopes: DeepLinkScope[]; problems: string[][] }) {
  const { t } = useTranslation();
  const setLink = (i: number, p: Partial<DeepLinkTemplate>) => patch({ deepLinks: body.deepLinks.map((l, j) => (j === i ? { ...l, ...p } : l)) });
  const add = () => {
    const used = new Set(body.deepLinks.map((l) => l.scope));
    const scope = scopes.find((s) => !used.has(s.key))?.key ?? "case";
    patch({ deepLinks: [...body.deepLinks, { key: scope, label: "", labelAr: null, template: "/", scope }] });
  };
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle>{t("admin:solutions.links.title")}</CardTitle><CardDescription>{t("admin:solutions.links.hint")}</CardDescription></div>
        {canWrite ? <Button variant="outline" size="sm" onClick={add}><Plus /> {t("admin:solutions.links.add")}</Button> : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {body.deepLinks.length === 0 ? <EmptyState icon={Link2} title={t("admin:solutions.links.empty")} description={t("admin:solutions.links.emptyHint")} /> : body.deepLinks.map((l, i) => (
          <LinkRow key={i} link={l} baseUrl={body.baseUrl} scopes={scopes} problems={problems[i] ?? []} canWrite={canWrite} onChange={(p) => setLink(i, p)} onRemove={() => patch({ deepLinks: body.deepLinks.filter((_, j) => j !== i) })} />
        ))}
      </CardContent>
    </Card>
  );
}

function LinkRow({ link, baseUrl, scopes, problems, canWrite, onChange, onRemove }: { link: DeepLinkTemplate; baseUrl: string; scopes: DeepLinkScope[]; problems: string[]; canWrite: boolean; onChange: (p: Partial<DeepLinkTemplate>) => void; onRemove: () => void }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement | null>(null);
  const scope = scopes.find((s) => s.key === link.scope);
  const insert = (placeholder: string) => {
    const el = input.current;
    const token = `{${placeholder}}`;
    const at = el?.selectionStart ?? link.template.length;
    const end = el?.selectionEnd ?? at;
    onChange({ template: link.template.slice(0, at) + token + link.template.slice(end) });
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(at + token.length, at + token.length); });
  };
  const message = (p: string) => (p.startsWith("placeholder:") ? t("admin:solutions.links.problem.placeholder", { name: `{${p.slice(12)}}`, scope: t(`admin:solutions.scope.${link.scope}`, { defaultValue: link.scope }) }) : t(`admin:solutions.links.problem.${p}`));
  return (
    <div className={cn("rounded-xl border p-3", problems.length ? "border-danger-200 bg-danger-50/30" : "border-sand-200 bg-white/70")}>
      <div className="grid gap-2 md:grid-cols-[140px_minmax(0,1fr)_minmax(0,1fr)_160px_auto]">
        <Field label={t("admin:common.key")}><Input value={link.key} dir="ltr" className="h-9 font-mono" readOnly={!canWrite} onChange={(e) => onChange({ key: e.target.value.toLowerCase() })} /></Field>
        <Field label={t("admin:solutions.links.label")}><Input value={link.label} className="h-9" readOnly={!canWrite} onChange={(e) => onChange({ label: e.target.value })} /></Field>
        <Field label={t("admin:solutions.links.labelAr")}><Input value={link.labelAr ?? ""} dir="rtl" className="h-9" readOnly={!canWrite} onChange={(e) => onChange({ labelAr: e.target.value || null })} /></Field>
        <Field label={t("admin:solutions.links.scope")}>
          <NativeSelect value={link.scope} className="h-9" disabled={!canWrite} onChange={(e) => onChange({ scope: e.target.value })}>{scopes.map((s) => <option key={s.key} value={s.key}>{t(`admin:solutions.scope.${s.key}`, { defaultValue: s.label })}</option>)}</NativeSelect>
        </Field>
        <div className="flex items-end">{canWrite ? <Tooltip content={t("admin:common.remove")}><Button size="icon-sm" variant="ghost" className="mb-1 text-danger-600" aria-label={t("admin:common.remove")} onClick={onRemove}><Trash2 /></Button></Tooltip> : null}</div>
      </div>
      <Field label={t("admin:solutions.links.template")} className="mt-2">
        <Input ref={input} value={link.template} dir="ltr" className="h-9 font-mono" readOnly={!canWrite} onChange={(e) => onChange({ template: e.target.value })} />
      </Field>
      {scope ? (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <span className="me-1 text-[11px] text-muted-foreground">{t("admin:solutions.links.placeholders")}</span>
          {scope.placeholders.map((p) => <button key={p} type="button" disabled={!canWrite} onClick={() => insert(p)} className="rounded-md border border-sand-200 bg-sand-50 px-1.5 py-0.5 font-mono text-[11px] text-aeblack-800 hover:border-aegold-300 hover:bg-aegold-50 disabled:cursor-default" dir="ltr">{`{${p}}`}</button>)}
        </div>
      ) : null}
      <p className="mt-2 truncate text-[11px] text-muted-foreground"><span className="me-1">{t("admin:solutions.links.preview")}</span><code className="font-mono text-aeblack-800" dir="ltr">{previewUrl(baseUrl, link.template)}</code></p>
      {problems.length ? <ul className="mt-1 space-y-0.5 text-[11px] text-danger-700">{problems.map((p) => <li key={p}>{message(p)}</li>)}</ul> : null}
    </div>
  );
}

function EmbedTab({ solution }: { solution: Solution }) {
  const { t } = useTranslation();
  const api = useApi();
  const apiBase = api.baseUrl || window.location.origin;
  const react = `import { useEffect } from "react";
import { AssistantPanel, useCaseThread } from "@ncp/assistant-sdk";

/** The signed-in officer's conversation about one case, filed under the "${solution.key}" surface. */
export function CaseAssistant({ caseId, onOpenDocument }: { caseId: string; onOpenDocument: (id: string) => void }) {
  const thread = useCaseThread("${solution.key}");
  useEffect(() => { thread.mutate({ caseId }); }, [caseId]);
  return <AssistantPanel threadId={thread.data?.id} compact onOpenDocument={onOpenDocument} />;
}`;
  const rest = `# 1. Find or open the caller's conversation about a case from this surface
POST ${apiBase}/api/assistant/threads/case/{caseId}
Authorization: Bearer <staff token>
{ "surfaceKey": "${solution.key}", "locale": "ar" }            # add "fresh": true for a new conversation

# 2. Ask; the answer streams back as server-sent events (token, tool, citation, proposal, confirm, final)
POST ${apiBase}/api/assistant/threads/{threadId}/messages
{ "message": "Summarise the parties' positions", "locale": "ar" }

# 3. Where to send the user for a record (resolved against this registry and the caller's access)
GET ${apiBase}/api/assistant/links?scope=case&caseId={caseId}`;
  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.embed.react")}</CardTitle><CardDescription>{t("admin:solutions.embed.reactHint")}</CardDescription></CardHeader>
        <CardContent><Snippet text={react} /></CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:solutions.embed.rest")}</CardTitle><CardDescription>{t("admin:solutions.embed.restHint")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Snippet text={rest} />
          {!solution.enabled ? <InlineAlert tone="warning">{t("admin:solutions.embed.disabled")}</InlineAlert> : null}
        </CardContent>
      </Card>
    </div>
  );
}

function Snippet({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="max-h-[420px] overflow-auto rounded-xl bg-aeblack-950 p-4 font-mono text-[11px] leading-relaxed text-sand-100 scrollbar-thin" dir="ltr">{text}</pre>
      <Button size="sm" variant="outline" className="absolute end-2 top-2 h-7 bg-white/90" onClick={() => { void copyText(text, t("admin:common.copied")).then((ok) => { if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); } }); }}>{copied ? t("admin:common.copied") : t("admin:common.copy")}</Button>
    </div>
  );
}
